import { useState } from "react";
import { NavLink, Link, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, FileText, CalendarCheck, Users, HeartHandshake, CalendarDays, Settings, LogOut, Menu, X } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../AuthContext";

const NAV = [
  { to: "/portal", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/portal/referrals", label: "Referrals", icon: FileText },
  { to: "/portal/consultations", label: "Consultations", icon: CalendarCheck },
  { to: "/portal/patients", label: "Patients", icon: Users },
  { to: "/portal/caregivers", label: "Caregivers", icon: HeartHandshake },
  { to: "/portal/schedule", label: "Schedule / Visits", icon: CalendarDays },
  { to: "/portal/settings", label: "Settings", icon: Settings },
];

export const AdminLayout = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
    navigate("/portal/login");
  };

  const navLinks = (onClick) =>
    NAV.map((item) => (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.end}
        onClick={onClick}
        data-testid={`admin-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        className={({ isActive }) =>
          `flex items-center gap-3 rounded-xl px-4 py-3 text-[15px] font-semibold transition-colors duration-300 ${
            isActive ? "bg-white/10 text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"
          }`
        }
      >
        <item.icon className="h-5 w-5 shrink-0" strokeWidth={1.6} />
        {item.label}
      </NavLink>
    ));

  return (
    <div data-testid="admin-portal" className="min-h-screen bg-brand-paper">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-brand-navy flex-col z-40">
        <Link to="/portal" className="flex items-center gap-3 px-6 pt-7 pb-6">
          <span className="bg-white rounded-xl p-1.5 shrink-0">
            <img src="/assets/bms-logo.png" alt="BMS logo" className="h-10 w-auto" />
          </span>
          <span className="leading-tight">
            <span className="block font-display font-bold text-white">BMS Health Care</span>
            <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-brand-cyan">Office Admin Portal</span>
          </span>
        </Link>
        <nav className="flex-1 px-4 space-y-1" aria-label="Admin">{navLinks()}</nav>
        <div className="px-4 pb-6">
          <div className="rounded-xl bg-white/5 px-4 py-3 mb-3">
            <p className="text-sm font-bold text-white truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="w-full flex items-center gap-3 rounded-xl px-4 py-3 text-[15px] font-semibold text-slate-300 hover:bg-white/5 hover:text-white transition-colors duration-300"
          >
            <LogOut className="h-5 w-5" strokeWidth={1.6} />
            Logout
          </button>
        </div>
      </aside>

      <div className="lg:hidden sticky top-0 z-40 bg-brand-navy flex items-center justify-between px-5 h-16">
        <Link to="/portal" className="flex items-center gap-2.5">
          <span className="bg-white rounded-lg p-1"><img src="/assets/bms-logo.png" alt="BMS logo" className="h-8 w-auto" /></span>
          <span className="font-display font-bold text-white text-sm">Office Admin</span>
        </Link>
        <button
          onClick={() => setOpen(!open)}
          data-testid="admin-mobile-menu-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          className="p-2 rounded-lg text-white hover:bg-white/10"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden bg-brand-navy px-4 pb-4 space-y-1 border-t border-white/10">
          {navLinks(() => setOpen(false))}
          <button
            onClick={logout}
            data-testid="admin-logout-mobile"
            className="w-full flex items-center gap-3 rounded-xl px-4 py-3 text-[15px] font-semibold text-slate-300 hover:bg-white/5 hover:text-white"
          >
            <LogOut className="h-5 w-5" strokeWidth={1.6} />
            Logout
          </button>
        </div>
      )}

      <main className="lg:ml-64 p-5 md:p-8 max-w-[1400px]">
        <Outlet />
      </main>
    </div>
  );
};
